import React, { useEffect, forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { IconInfoCircle, IconPlus, IconTable } from '@tabler/icons-react';
import {
  Title,
  Tooltip,
  Button,
  Loader,
  Divider,
  MultiSelect,
  Select,
  Group,
  Stack,
  NumberInput,
  Text,
  Grid,
  Center,
  Box,
} from '@mantine/core';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import LawyerTable from './LawyerTable';
import ExperienceTable from './ExperienceTable';
import { getLawyerList } from '../../../../services/proposalsRoutes';
import { authWebSocket } from '../../../../auth/authFetches';
import { PROPOSALACTIONS } from '../../reducers/proposalReducer';
import { USERFEEDBACKACTIONS } from '../../reducers/userFeedbackReducer';
import EditableTable from '../../../../components/Tables/EditableTable';

import { OtherData, Proposal } from '../../../../interfaces/types';
import { styles } from '../../styles';
import { OTHERDATAACTIONS } from '../../reducers/otherDataReducer';
import { GenerateLawyers } from '../Buttons/GenerateLawyers';
import { GenerateExperiences } from '../Buttons/GenerateExperiences';
import { useProposal } from '../../context/ProposalContext';
import ActionButtons from './ActionButtons';

export default function ThirdTab() {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const {
    proposal,
    dispatchProposal,
    otherData,
    dispatchOtherData,
    userFeedback,
    dispatchUserFeedback,
  } = useProposal();
  const { lawyers, experience, input, sector, constraints, language } = proposal;
  const { allLawyers } = otherData;
  const { loadingLawyers, loadingExperiences, lawyerLoadingState, experienceLoadingState } =
    userFeedback;

  return (
    <Stack p="md" mih="600px">
      <Grid columns={13}>
        {/* Left Column - Lawyers */}
        <Grid.Col span={6}>
          <Stack>
            <Group justify="space-between">
              <Title order={4}>{t('lawyers')}</Title>
              {loadingLawyers && (
                <Text size="xs">
                  {capitalize(lawyerLoadingState)}
                  {lawyerLoadingState ? '...' : ''}
                </Text>
              )}
              <Group>
                <GenerateLawyers />
                <Tooltip label={t('viewLawyerExpertiseTable')}>
                  <Button component={Link} to="/lawyertable" variant="light" color="black" px="sm">
                    <IconTable size={20} />
                  </Button>
                </Tooltip>
              </Group>
            </Group>
            <LawyerTable allLawyers={allLawyers} selectedLawyers={lawyers} />

            {/* Special Roles Section */}
            {/* <Stack mt="md">
              <Title order={4}>
                {t('specialRoles')}
                <Tooltip label={t('specialRolesInfo')} withArrow multiline position="right" w={220}>
                  <IconInfoCircle
                    size={20}
                    color="var(--mantine-primary-color-filled)"
                    style={{ marginLeft: '0.5rem' }}
                  />
                </Tooltip>
              </Title>
              <Stack>
                <Select
                  label={t('partnerResponsible')}
                  data={lawyers.map((lawyer) => ({ value: lawyer.name, label: lawyer.name }))}
                  value={proposal.specialRoles?.partnerResponsible || null}
                  onChange={(value) => {
                    dispatchProposal({
                      type: PROPOSALACTIONS.SET_FIELD,
                      field: 'specialRoles',
                      value: {
                        ...proposal.specialRoles,
                        partnerResponsible: value,
                      },
                    });
                  }}
                  placeholder={t('selectLawyer')}
                  searchable
                  clearable
                />
                <Select
                  label={t('lawyerResponsible')}
                  data={lawyers.map((lawyer) => ({ value: lawyer.name, label: lawyer.name }))}
                  value={proposal.specialRoles?.lawyerResponsible || null}
                  onChange={(value) => {
                    dispatchProposal({
                      type: PROPOSALACTIONS.SET_FIELD,
                      field: 'specialRoles',
                      value: {
                        ...proposal.specialRoles,
                        lawyerResponsible: value,
                      },
                    });
                  }}
                  placeholder={t('selectLawyer')}
                  searchable
                  clearable
                />
              </Stack>
            </Stack> */}
          </Stack>
        </Grid.Col>

        {/* Vertical Divider */}
        <Grid.Col span={1}>
          <Center h="100%" w="100%">
            <Center h="100%" w="100%">
              <Divider orientation="vertical" h="100%" />
            </Center>
          </Center>
        </Grid.Col>

        {/* Right Column - Experience */}
        <Grid.Col span={6}>
          <Stack>
            <Group justify="space-between">
              <Title order={4}>{t('experience')}</Title>
              <Group>
                {loadingExperiences && (
                  <Text size="xs">
                    {capitalize(experienceLoadingState)}
                    {experienceLoadingState ? '...' : ''}
                  </Text>
                )}
                <GenerateExperiences />
              </Group>
            </Group>
            <ExperienceTable experiences={experience} />
          </Stack>
        </Grid.Col>
      </Grid>
      <ActionButtons />
    </Stack>
  );
}

function capitalize(str: string | undefined | null) {
  if (!str) return '';
  const firstLetter = str.slice(0, 1).toLocaleUpperCase();
  if (str.length === 1) return firstLetter;
  const rest = str.slice(1);
  return `${firstLetter}${rest}`;
}
